import { createComponent, inject, Service, ComponentRef, ApplicationRef, OnDestroy } from '@angular/core';
import { BehaviorSubject, debounceTime, distinctUntilChanged, map, Subscription, tap, timer } from 'rxjs';
import { loaderStart$, loaderStop$ } from '../operators/handle-loader';
import { LoaderType } from '../types/loader.type';
import { OverlayComponent } from '../../overlay/overlay.component';
import { LoaderComponent } from '../loader.component';
import { OverlayService } from '../../overlay/overlay.service';

@Service()
export class LoaderService implements OnDestroy {
    private readonly applicationRef = inject(ApplicationRef);
    private readonly overlayService = inject(OverlayService);
    private overlayComponentRef: ComponentRef<OverlayComponent> | null = null;
    private overlayId: string | null = null;
    private destroySubscription: Subscription | null = null;

    private readonly loaders$ = new BehaviorSubject<LoaderType[]>([]);
    private readonly isLoading$ = this.loaders$.pipe(
        map((loaders) => loaders.length > 0),
        debounceTime(25),
        distinctUntilChanged(),
    );

    private subscriptions$ = new Subscription();

    constructor() {
        this.subscriptions$.add(loaderStart$.pipe(tap((id) => this.startLoading(id))).subscribe());
        this.subscriptions$.add(loaderStop$.pipe(tap((id) => this.stopLoading(id))).subscribe());
        this.subscriptions$.add(this.isLoading$.pipe(tap((isLoading) => this.isLoading(isLoading))).subscribe());
    }

    ngOnDestroy(): void {
        this.subscriptions$.unsubscribe();
        this.destroySubscription?.unsubscribe();
    }

    startLoading(id: LoaderType): void {
        this.loaders$.next([...this.loaders$.getValue(), id]);
    }

    stopLoading(id: LoaderType): void {
        this.loaders$.next(this.loaders$.getValue().filter((loaderId) => loaderId !== id));
    }

    isLoading(isLoading: boolean) {
        if (isLoading === true) {
            this.appendLoaderComponent();
        } else {
            this.removeLoaderComponent();
        }
    }

    appendLoaderComponent() {
        // Loading resumed: cancel a pending destroy and fade the existing overlay back in.
        this.destroySubscription?.unsubscribe();
        this.destroySubscription = null;

        if (this.overlayComponentRef !== null) {
            this.overlayComponentRef.setInput('animationState', 'visible');
            this.overlayComponentRef.changeDetectorRef.detectChanges();
            return;
        }

        if (this.overlayService.active()) {
            return;
        }

        this.overlayId = this.overlayService.register();

        const loaderComponentRef = createComponent(LoaderComponent, {
            environmentInjector: this.applicationRef.injector,
        });

        loaderComponentRef.changeDetectorRef.detectChanges();

        this.overlayComponentRef = createComponent(OverlayComponent, {
            environmentInjector: this.applicationRef.injector,
            projectableNodes: [[loaderComponentRef.location.nativeElement]],
        });

        this.overlayComponentRef.setInput('animationState', 'visible');

        this.applicationRef.attachView(this.overlayComponentRef.hostView);
        document.body.appendChild(this.overlayComponentRef.location.nativeElement);
    }

    removeLoaderComponent() {
        if (this.overlayComponentRef === null) {
            return;
        }

        this.overlayComponentRef.setInput('animationState', 'hidden');
        this.overlayComponentRef.changeDetectorRef.detectChanges();

        // A timer, not `transitionend` — a 0→0 opacity change fires no event and would strand the overlay.
        const nativeElement = this.overlayComponentRef.location.nativeElement;
        const durationMs = parseFloat(getComputedStyle(nativeElement).transitionDuration) * 1000;
        this.destroySubscription = timer(durationMs)
            .pipe(tap(() => this.destroyOverlay()))
            .subscribe();
    }

    private destroyOverlay() {
        if (this.overlayComponentRef !== null) {
            this.overlayService.unregister(this.overlayId!);
            this.overlayId = null;
            this.applicationRef.detachView(this.overlayComponentRef.hostView);
            this.overlayComponentRef.destroy();
            this.overlayComponentRef = null;
        }
    }
}
